import styled from 'styled-components';
import React from 'react';

import InsightPropTypes from '../../models/Insight/InsightPropTypes';
import { fontSizes, media, colors, InternalLink, Card, CardContent, Heading, Text } from '../../ui-components';
import HeadingCategoryAndDate from '../heading-category-and-date';
import { truncateText } from '../../utils/truncate-text';

const propTypes = {
  insight: InsightPropTypes
};

/**
 * Component that renders an insight card for Whitepaper, Ebook, or Infographic types
 *
 * @component DownloadableContentCard
 * 
 * @param {object} props
 * 
 * @prop {object} insight
 * @prop {object} className
 */
const DownloadableContentCard = props => {
  const { className, insight } = props;

  const infographic = insight.type === 'Infographic';
  const backgroundColor =  infographic ? colors.lightSky : colors.darkOcean;
  const fill = infographic ? colors.crederaRed : colors.pureWhite;
  const textFill = infographic ? colors.labelDefault : colors.pureWhite;
  const textColor = infographic ? colors.midnightBlack : colors.pureWhite;

  return (
    <FlexCard backgroundColor={backgroundColor} textColor={textColor} textFill={textFill}>
      <LinkWrapper className={className} to={`/insights/${insight.slug}`}>
        <ContentCardContent backgroundColor={backgroundColor}>
          <HeadingCategoryAndDate insight={insight} fill={fill} textFill={textFill} />
          <TitleHeading level={5} textColor={textColor}>
            {insight.title}
          </TitleHeading>
          <SnippetText size={'medium'} textColor={textColor}>{truncateText(insight.snippet, 175)}</SnippetText>
          <InternalLink className={'insight-link'} linkstyle={'primary-link'} to={`/insights/${insight.slug}`} withArrow hoverUnderline>
            {`GET THE ${insight.type}`}
          </InternalLink>
        </ContentCardContent>
      </LinkWrapper>
    </FlexCard>
  );
};

const FlexCard = styled(props => <Card {...props} />)`
  flex: 1;
  position: relative;
  
  &:hover {
    a, svg {
      color: ${props => props.backgroundColor === colors.lightSky ? colors.buttonHover : colors.pureWhite};
    }

    a.insight-link {
      text-decoration: underline;
    }

    h5, p {
      color: ${props => props.textColor};
    }
    
    p.category-name, p.publish-date {
      color: ${props => props.textFill};
    }
  }
`;

const LinkWrapper = styled(props => <InternalLink {...props} />)`
  text-decoration: none;
  text-transform: none;
  height: 100%;
  box-shadow: 0 0 80px 0 rgba(58, 58, 58, 0.06);
`;

const ContentCardContent = styled(CardContent)`
  overflow: hidden;
  width: 100%;

  & > * {
    position: relative;
    z-index: 1;
    color: ${props => props.backgroundColor === colors.lightSky ? colors.buttonDefault : colors.pureWhite};  }
    
  a {
    display: flex;
    justify-content: flex-start;
    align-items: flex-end;
    width: 100%;
    height: 100%;
    bottom: 0;
  }
`;

const TitleHeading = styled(props => <Heading {...props} />)`
  color: ${props => props.textColor};
  padding: 4px 0 8px 0;
  
  @media (max-width: ${media.smallTop}) {
    line-height: ${fontSizes.h5.lineHeight};
  }
`;

const SnippetText = styled(props => <Text {...props} />)`
  color: ${props => props.textColor};
`;

DownloadableContentCard.propTypes = propTypes;

export default DownloadableContentCard;
